import { useState } from 'react';
import { ExternalLink } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTranslation } from 'react-i18next';
import { trpc } from '@/lib/trpc';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { BRAIN_LINK_CITATION_PREFIX, type BrainCitation } from '../../../../shared/types/brain';
import { brainSourceOrManual } from '../../../../shared/brain-sources';
import { BRAIN_SOURCE_LABEL_KEYS, brainSourceUi } from './brain-source-ui';
import { Link } from 'react-router-dom';

interface BrainCitationCardProps {
  citation: BrainCitation;
}

export function BrainCitationCard({ citation }: BrainCitationCardProps) {
  const [open, setOpen] = useState(false);
  const { t, i18n } = useTranslation();

  // Ícono, tono y etiqueta salen del registro de fuentes (shared/brain-sources.ts);
  // una fuente vieja o desconocida en Aurora se muestra como "manual".
  const sourceId = brainSourceOrManual(citation.source);
  const { Icon, ...cfg } = brainSourceUi(sourceId);
  const sourceLabel = t(BRAIN_SOURCE_LABEL_KEYS[sourceId]);

  const dateLabel = citation.fecha
    ? new Date(citation.fecha + 'T00:00:00').toLocaleDateString(i18n.language, { month: 'short', year: '2-digit' })
    : null;

  // Cita de un área (landing de People, dashboard de Finance): es un link interno,
  // no una página del Brain — se abre la sección en vez del panel lateral.
  const link = citation.pageId.startsWith(BRAIN_LINK_CITATION_PREFIX) ? citation.pageId.slice(BRAIN_LINK_CITATION_PREFIX.length) : null;

  const { data: page, isLoading } = trpc.brain.getPage.useQuery(
    { pageId: citation.pageId },
    { enabled: open && !link }
  );

  // Try to extract a Slack URL from page content (stored in markdown as [ver en Slack](url) or similar)
  const slackUrl = page?.content
    ? (page.content.match(/https:\/\/winclap\.slack\.com\/archives\/[^\s)]+/) ?? [])[0]
    : undefined;

  if (link) {
    return (
      <Link
        to={link}
        className={`shrink-0 w-52 rounded-lg border ${cfg.border} ${cfg.bg} p-3 space-y-1.5 hover:shadow-md hover:brightness-105 transition-all text-left group`}
      >
        <div className="flex items-center justify-between gap-1">
          <div className={`flex items-center gap-1.5 ${cfg.color}`}>
            <Icon className="h-3.5 w-3.5" />
            <span className="text-xs font-medium">{t('brain.citation.internal_link')}</span>
          </div>
          <ExternalLink className="h-2.5 w-2.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
        <p className="text-xs font-medium text-foreground line-clamp-2 leading-snug">{citation.title}</p>
        {citation.excerpt && <p className="text-[11px] text-muted-foreground line-clamp-2 leading-snug">{citation.excerpt}</p>}
      </Link>
    );
  }

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={`shrink-0 w-52 rounded-lg border ${cfg.border} ${cfg.bg} p-3 space-y-1.5 hover:shadow-md hover:brightness-105 transition-all cursor-pointer text-left group`}
      >
        <div className="flex items-center justify-between gap-1">
          <div className={`flex items-center gap-1.5 ${cfg.color}`}>
            <Icon className="h-3.5 w-3.5" />
            <span className="text-xs font-medium">{sourceLabel}</span>
          </div>
          <div className="flex items-center gap-1">
            {dateLabel && (
              <span className="text-[10px] text-muted-foreground">{dateLabel}</span>
            )}
            <ExternalLink className="h-2.5 w-2.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
        </div>
        <p className="text-xs font-medium text-foreground line-clamp-2 leading-snug">
          {citation.title}
        </p>
        {citation.excerpt && (
          <p className="text-[11px] text-muted-foreground line-clamp-2 leading-snug">
            {citation.excerpt}
          </p>
        )}
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent>
          <SheetHeader>
            <div className={`inline-flex items-center gap-1.5 ${cfg.color} mb-1`}>
              <Icon className="h-3.5 w-3.5" />
              <span className="text-xs font-medium">{sourceLabel}</span>
              {dateLabel && <span className="text-xs text-muted-foreground">· {dateLabel}</span>}
            </div>
            <SheetTitle>{citation.title}</SheetTitle>
            {slackUrl && (
              <SheetDescription>
                <a
                  href={slackUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-primary hover:underline"
                >
                  <ExternalLink className="h-3 w-3" />
                  {t('brain.citation.view_slack')}
                </a>
              </SheetDescription>
            )}
          </SheetHeader>

          <div className="flex-1 overflow-y-auto px-6 py-4">
            {isLoading ? (
              <div className="flex items-center justify-center h-32 text-sm text-muted-foreground animate-pulse">
                {t('brain.citation.loading')}
              </div>
            ) : page ? (
              <div className="prose prose-sm dark:prose-invert max-w-none [&_table]:text-xs [&_th]:font-semibold [&_td]:py-1 [&_code]:text-primary [&_code]:bg-primary/10 [&_code]:px-1 [&_code]:rounded">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{page.content}</ReactMarkdown>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">{t('brain.citation.error')}</p>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
